import Enumerable from 'linq';

import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { AvatarMode } from '../avatar.component';
import { colorsUtilityBorderGrayStrong, colorsUtilityTextBlueStrong, colorsUtilityTextBlueSubtle } from '@workbuddy/ui/styles-js';

export type SvgAvatarOption = { abbreviation?: boolean; bgColor?: string, frColor?: string, border?: boolean }



@Pipe({
    name: 'svgAvatar',
    standalone: true
})
export class SvgAvatarPipe implements PipeTransform {

    private readonly sanitizer = inject(DomSanitizer);

    transform(value: string, mode = AvatarMode.Letter, border = false): SafeResourceUrl {

        value = value || '';

        if (mode === AvatarMode.Image) {
            return value;
        }

        let abbreviation: boolean,
            bgColor: string,
            frColor: string;

        switch (mode) {
            case AvatarMode.Letter:
                value = value.replace(/[^a-zA-Z]/g, '');
                abbreviation = true;
                break;
            case AvatarMode.Text:
                bgColor = hexToRgb(colorsUtilityTextBlueSubtle);
                frColor = hexToRgb(colorsUtilityTextBlueStrong);
                break;
            case AvatarMode.Icon:
                bgColor = hexToRgb(colorsUtilityTextBlueSubtle);
                frColor = hexToRgb(colorsUtilityTextBlueStrong);
                break;
        }


        const avatar = generateSvgDataUri(value, { abbreviation, bgColor, frColor, border });
        // console.log('avatar', this.sanitizer.bypassSecurityTrustResourceUrl(avatar));
        return this.sanitizer.bypassSecurityTrustResourceUrl(avatar);

    }

}


/** ============= ============= ============= ============= ============= ============= ============= */

const colors = ['#1abc9c', '#16a085', '#f1c40f', '#f39c12', '#2ecc71', '#27ae60', '#e67e22', '#d35400', '#3498db', '#2980b9', '#e74c3c', '#c0392b', '#9b59b6', '#8e44ad', '#bdc3c7', '#34495e', '#2c3e50', '#95a5a6', '#7f8c8d', '#ec87bf', '#d870ad', '#f69785', '#9ba37e', '#b49255', '#b49255', '#a94136'];

function generateSvgDataUri(text: string, option?: SvgAvatarOption) {
    const svg = generateSvg(text, option);
    const prefix = 'data:image/svg+xml;utf8,';
    return `${prefix}${svg}`;
}


function generateSvg(text: string, option?: SvgAvatarOption): string {
    const bgColor = option?.bgColor || generateAvatarColor(text);
    const frColor = option?.frColor || 'white';
    text = option?.abbreviation ? generateAvatarAbbreviation(text) : text;

    const borderAttr = option?.border ? `stroke-dasharray="10 3" stroke="${hexToRgb(colorsUtilityBorderGrayStrong)}" stroke-width="1"` : '';
    const fonts = 'Inter, Proxima Nova, proxima-nova, HelveticaNeue-Light, Helvetica Neue Light, Helvetica Neue, Helvetica, Arial, Lucida Grande, sans-serif';

    const body = ''
        + `<circle cx="15" cy="15" r="15" fill="${bgColor}" ${borderAttr} />`
        + `<text transform="translate(15 20)" text-anchor="middle" fill="${frColor}" font-size="12" font-family="${fonts}">${text.toUpperCase()}</text>`

    return `<svg xmlns="http://www.w3.org/2000/svg" height="30" width="30">${body}</svg>`;

}


function generateAvatarAbbreviation(text: string): string {
    if (!text)
        text = '??';

    // Ensure we have intials
    const split = text.split(' ');
    text = split.length > 0
        ? Enumerable
            .from(split)
            .select(x => x.trim())
            .where(x => x !== '' && x !== '&')
            .take(2)
            .select(x => x[0])
            .toArray()
            .join('')
        : text;

    return text;
}

function generateAvatarColor(text: string) {

    const colorIndex = Math.floor((text.charCodeAt(0) - 65) % colors.length);
    let hex = colors[Math.abs(colorIndex)];

    if (!hex) {
        hex = '#ffffff';
    }

    const finalColor = hexToRgb(hex);
    return finalColor;

}

function hexToRgb(hex) {
    if (!hex) {
        return "rgb(0, 0, 0)";
    }

    if (hex.startsWith('rgb')) {
        return hex;
    }

    hex = hex.replace(/^#/, '');

    if (hex.length === 3) {
        hex = hex.replace(/(.)/g, '$1$1');
    } else if (hex.length !== 6) {
        return "rgb(0, 0, 0)";
    }

    const bigint = parseInt(hex, 16);
    // tslint:disable-next-line:no-bitwise
    const r = (bigint >> 16) & 255;
    // tslint:disable-next-line:no-bitwise
    const g = (bigint >> 8) & 255;
    // tslint:disable-next-line:no-bitwise
    const b = bigint & 255;

    return `rgb(${r}, ${g}, ${b})`;
}
